import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useUser } from '@clerk/clerk-react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import { searchService, type SearchResponse, type SearchFilters, type SearchResult } from '@/lib/api/services'

interface SearchHistoryItem {
  id: string
  query: string
  results: SearchResult[]
  timestamp: string
}

export function useSearchGeniuses() {
  const { user } = useUser()
  const queryClient = useQueryClient()

  // Get search history from cache
  const searchHistory = (queryClient.getQueryData<SearchHistoryItem[]>(['searchHistory']) || [])
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  
  // Get selected search ID from cache
  const selectedSearchId = queryClient.getQueryData<string | null>(['selectedSearchId']) || null

  const searchMutation = useMutation({
    mutationFn: async ({ 
      jobDescription, 
      filters 
    }: { 
      jobDescription: string; 
      filters?: SearchFilters;
    }): Promise<SearchResponse> => {
      if (!user?.id) throw new Error('User not authenticated')

      // Use the new API service
      return await searchService.searchGeniuses({
        jobDescription,
        filters,
      })
    },
  })

  const searchGeniuses = (
    jobDescription: string, 
    filters?: SearchFilters
  ): string => {
    const searchId = uuidv4()
    
    // Create optimistic entry immediately
    const optimisticEntry: SearchHistoryItem = {
      id: searchId,
      query: jobDescription,
      results: [],
      timestamp: new Date().toISOString(),
    }
    
    // Add to history optimistically
    const currentHistory = queryClient.getQueryData<SearchHistoryItem[]>(['searchHistory']) || []
    queryClient.setQueryData<SearchHistoryItem[]>(['searchHistory'], [
      optimisticEntry,
      ...currentHistory,
    ])
    
    // Start the actual search
    searchMutation.mutate({ jobDescription, filters }, {
      onSuccess: (data) => {
        // Update the optimistic entry with real results
        const currentHistory = queryClient.getQueryData<SearchHistoryItem[]>(['searchHistory']) || []
        const updatedHistory = currentHistory.map(item => 
          item.id === searchId
            ? { ...item, results: data.results, timestamp: data.timestamp }
            : item
        )
        queryClient.setQueryData<SearchHistoryItem[]>(['searchHistory'], updatedHistory)
        
        if (data.results.length === 0) {
          toast.info('No matching experts found. Try adjusting your search criteria.')
        } else {
          toast.success(`Found ${data.results.length} matching AI experts!`)
        }
      },
      onError: (error) => {
        // Remove the optimistic entry on error
        const currentHistory = queryClient.getQueryData<SearchHistoryItem[]>(['searchHistory']) || []
        const revertedHistory = currentHistory.filter(item => item.id !== searchId)
        queryClient.setQueryData<SearchHistoryItem[]>(['searchHistory'], revertedHistory)
        
        console.error('Search failed:', error)
        toast.error('Search failed. Please try again.')
      }
    })
    
    return searchId
  }

  const clearHistory = () => {
    queryClient.setQueryData<SearchHistoryItem[]>(['searchHistory'], [])
    toast.success('Search history cleared')
  }

  const currentResults = (searchId: string) => {
    return searchHistory.find(item => item.id === searchId)
  }

  const setSelectedSearchId = (searchId: string | null) => {
    queryClient.setQueryData<string | null>(['selectedSearchId'], searchId)
  }

  return {
    searchGeniuses,
    isSearching: searchMutation.isPending,
    searchHistory,
    currentResults,
    clearHistory,
    selectedSearchId,
    setSelectedSearchId,
  }
}